import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth, AuthContextValue } from "../../providers/AuthProvider";
import axiosBaseURL from "..";

export const useFarmingStagesByProductId = (productId: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["product_farming_stage", productId],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/operation/product/${productId}/farming-stages`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useGetFarmingStageById = (id?: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["farming-stage", id],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/operation/farming-stage/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetFarmingStageList = () => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["farming stage list"],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/operation/farming-stage`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetFarmingStageByOrderId = (orderId?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["get_tracking_data_by_order_id", orderId],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/operation/product/${orderId}/farming-stage`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useAddProductFarmingStage = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (stage: {
      farmingStageId: number;
      productId: number;
    }) => {
      const result = await axiosBaseURL.post(
        `/operation/product/farming-stage`,
        stage,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useCreateFarmingStage = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (stage: {
      stageName: string;
      description: string;
      detail: string;
    }) => {
      const result = await axiosBaseURL.post(
        `/operation/farming-stage`,
        { id: null, ...stage },
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useUpdateFarmingStage = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useGetFarmingStageList();
  return useMutation({
    mutationFn: async (data: {
      id: number;
      stage: { stageName?: string; description?: string; detail?: string };
    }) => {
      const result = await axiosBaseURL.patch(
        `/operation/farming-stage?id=${data.id}`,
        data.stage,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      refetch();
      return result;
    },
  });
};

export const useDeleteFarmingStage = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useGetFarmingStageList();
  return useMutation({
    mutationFn: async (id: number) => {
      const result = await axiosBaseURL.delete(
        `/operation/farming-stage/${id}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      refetch();
      return result;
    },
  });
};

export const useDisassociateFarmingStageFromProduct = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useGetFarmingStageList();
  return useMutation({
    mutationFn: async (id: number) => {
      const result = await axiosBaseURL.delete(
        `/operation/farming-stage-product/${id}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      refetch();
      return result;
    },
  });
};
